import { observer } from "mobx-react";
import { Box } from "@mui/material";
import ContentBlock from "../components/ContentBlock";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import SickdayDayList from "../components/lists/SickdayDayList";
import BarChart from "../components/charts/BarChart";
import GenericForm from "../components/forms/GenericForm";
import { FieldGroupId } from "../lib/enums";
import {
  FormGetField,
  FormGetResponse,
  FormSaveRequest,
  FormSaveRequestSchema,
  FormsGetResponse,
} from "../models/Form";
import { useStoreContext } from "..";
import InfoText from "../components/typographies/InfoText";
import { useTranslation } from "react-i18next";

const SickDays = observer(() => {
  const { t } = useTranslation();
  const [currentData, setCurrentData] = useState<FormSaveRequest | undefined>(
    undefined
  );
  const [clientFormId, setClientFormId] = useState<number | undefined>(
    undefined
  );
  const [formDatas, setFormDatas] = useState<FormsGetResponse | null>(null);
  const [listFormDatas, setListFormDatas] = useState<FormsGetResponse | null>(
    null
  );
  const { formStore, fieldStore } = useStoreContext();

  useEffect(() => {
    const fetchFields = async () => {
      if (!formStore.forms) {
        await formStore.getForms();
      }
      if (!fieldStore.fields) {
        await fieldStore.getFields();
      }

      const forms = await formStore.getFormsByFormId(
        FieldGroupId.SICKDAYS_FIELD_GROUP_ID
      );
      const fields = fieldStore.fields;
      if (forms && fields) {
        setFormDatas(forms);
        const populatedForms = forms.map((form) => {
          const formValues = form.values as unknown as FormGetField[];
          const populatedValues = formValues.map((formValue) => {
            const field = fields.find((f) => f.alias === formValue.fieldAlias);
            const optionName = field?.fieldOptions?.find(
              (option) => option.id == formValue.value
            )?.name;
            if (!optionName) {
              return formValue;
            }
            return { ...formValue, value: optionName };
          });
          return { ...form, values: populatedValues };
        });
        if (populatedForms) {
          setListFormDatas(populatedForms);
        }
      }
    };
    fetchFields();
  }, [formStore.forms]);

  const handleSelectDay = (day: FormGetResponse) => {
    const form = formDatas?.find((f) => f.clientFormId === day.clientFormId);
    const initialForm = {
      formId: FieldGroupId.SICKDAYS_FIELD_GROUP_ID,
      values:
        form?.values.map((v) => {
          if (
            v.fieldAlias === "D_Sjukskrivning_1_Start_Datum" ||
            v.fieldAlias === "D_Sjukskrivning_2_Slut_Datum"
          ) {
            return {
              fieldAlias: v.fieldAlias,
              value: dayjs(v.value).format("YYYY-MM-DD"),
            };
          }
          return v;
        }) || [],
    };
    if (initialForm) {
      setCurrentData(initialForm);
      setClientFormId(day.clientFormId);
    }
  };

  const onSave = (data: any) => {
    const preppedData = {
      ...data,
      values: data.values.filter((v: any) => v.value !== null),
    };
    formStore.saveForm(preppedData as FormSaveRequest);
  };

  const onUpdate = (data: any) => {
    const preppedData = {
      ...data,
      values: data.values.filter((v: any) => v.value !== null),
    };
    if (preppedData && clientFormId) {
      formStore.updateForm(clientFormId, preppedData as FormSaveRequest);
    }
  };

  return (
    <Box className="page-container">
      <Box sx={{ marginBottom: 2 }}>
        <InfoText text={t("sickdays.info")} />
      </Box>
      <ContentBlock title="Registrera: Sjukdagar" icon="edit" bottomMargin>
        <GenericForm
          currentData={currentData as any}
          zodDataSchema={FormSaveRequestSchema}
          formId={FieldGroupId.SICKDAYS_FIELD_GROUP_ID}
          onDeSelect={() => {
            setCurrentData(undefined);
            setClientFormId(undefined);
          }}
          onUpdate={onUpdate}
          onSave={onSave}
        />
      </ContentBlock>
      <ContentBlock title="Sjukdagar" icon="list" bottomMargin>
        <SickdayDayList
          items={listFormDatas as any}
          onClick={handleSelectDay}
        />
      </ContentBlock>

      <ContentBlock title="Frånvaro från arbete/skola" icon="graph">
        {formDatas && (
          <BarChart
            items={formDatas as any}
            xAxisKey="D_Sjukskrivning_1_Start_Datum"
            valueKey="D_Sjukskrivning_NumberOfDays"
          />
        )}
      </ContentBlock>
    </Box>
  );
});

export default SickDays;
